import { useQuery } from '@tanstack/react-query';
import { collaborationsApi, runExplorerApi } from '@/services/api';
import type { RunDetail } from '@/types/runExplorer';

/**
 * Run Explorer data (task 1151, step 1).
 *
 * The page loads a run whole — one `GET /collaborations/:collaborationId/runs/:runId`
 * (task 1150) — and fetches each journal entry's large text fields (the rendered
 * prompt, the raw reply, a tool's result) only when the User opens them.
 */

/** Statuses a run is still moving through; the polling variant refetches while the run has one. */
const IN_PROGRESS_STATUSES = new Set(['in_progress', 'running', 'pending']);

/** How often an in-progress run is re-read. Realtime updates are out of scope for task 1151. */
export const RUN_POLL_INTERVAL_MS = 5000;

export const runStatusOf = (detail: RunDetail | undefined): string | undefined =>
  detail?.run?.status ?? detail?.collaboration?.status;

export const isRunInProgress = (detail: RunDetail | undefined): boolean =>
  IN_PROGRESS_STATUSES.has(runStatusOf(detail) ?? '');

const runDetailQuery = (collaborationId: string | undefined, runId: string | undefined) => ({
  queryKey: ['run-detail', collaborationId, runId],
  queryFn: () => runExplorerApi.getRunDetail(collaborationId!, runId!).then((res) => res.data.data),
  enabled: !!collaborationId && !!runId,
});

/** The whole run, read once. */
export const useRunDetail = (collaborationId: string | undefined, runId: string | undefined) =>
  useQuery(runDetailQuery(collaborationId, runId));

/**
 * The whole run, re-read every five seconds while it is in progress and not
 * after it finishes.
 */
export const usePollingRunDetail = (collaborationId: string | undefined, runId: string | undefined) =>
  useQuery({
    ...runDetailQuery(collaborationId, runId),
    refetchInterval: (query) => (isRunInProgress(query.state.data) ? RUN_POLL_INTERVAL_MS : false),
  });

/**
 * One journal entry with its large text fields, fetched on demand: pass
 * `enabled` only once the User expands the entry or the section.
 */
export const useJournalEntryDetail = (
  collaborationId: string | undefined,
  entryId: string | undefined,
  enabled = true
) =>
  useQuery({
    queryKey: ['collaboration-journal', collaborationId, entryId],
    queryFn: () => collaborationsApi.getJournal(collaborationId!, entryId!).then((res) => res.data.data),
    enabled: enabled && !!collaborationId && !!entryId,
    staleTime: Infinity,
  });
